import React, { useEffect, useState } from 'react';
import { ChoiceTile, InputTextArea, Label, Modal, SparkIcon, SparkModalPortal, TaskTypeIcon, sparkToast } from '@spark';
import { useAuth } from '@/features/auth/AuthContext';
import { createCustomerIntent } from '../api/customerIntentsApi';
import {
    CUSTOMER_INTENT_AUTONOMY_LABEL,
    CUSTOMER_INTENT_AUTONOMY_LEVELS,
    CUSTOMER_INTENT_FEATURES,
    CUSTOMER_INTENT_FEATURES_LABEL,
    CUSTOMER_INTENT_MODAL_TITLE,
    CUSTOMER_INTENT_MODAL_WIDTH,
    CUSTOMER_INTENT_SEND_FAILED_MESSAGE,
    CUSTOMER_INTENT_SENT_MESSAGE,
    CUSTOMER_INTENT_SOMETHING_ELSE_LABEL,
    CUSTOMER_INTENT_SOMETHING_ELSE_MAX_LENGTH,
    CUSTOMER_INTENT_SOMETHING_ELSE_PLACEHOLDER,
    hasCustomerIntentContent,
} from '../constants';
import '../customer-intents.css';

const toggle = (list, value) => (list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);

/**
 * "What should your model do next?" (Figma 9036-10012): multi-select feature
 * cards, single-select autonomy tiles and a free-text field. Opened from the
 * "Contact us" link in the SaaS-gated task-type / autonomy tooltips; files a
 * ClickUp task on the customers-intents list via ``POST /customer-intents``.
 */
export default function CustomerIntentModal({ open, onClose }) {
    // `useAuth()` is null outside the provider (unit tests, self-hosted shells).
    const workspace = useAuth()?.workspace;
    const [features, setFeatures] = useState([]);
    const [autonomyLevel, setAutonomyLevel] = useState(null);
    const [somethingElse, setSomethingElse] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        setFeatures([]);
        setAutonomyLevel(null);
        setSomethingElse('');
        setSubmitting(false);
    }, [open]);

    const canSubmit = !submitting && hasCustomerIntentContent({ features, autonomyLevel, somethingElse });
    const close = () => (submitting ? null : onClose?.());

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await createCustomerIntent({
                features,
                autonomyLevel,
                somethingElse: somethingElse.trim(),
                workspace: workspace?.name ?? workspace?.id ?? null,
            });
            sparkToast.success(CUSTOMER_INTENT_SENT_MESSAGE);
            onClose?.();
        } catch (err) {
            sparkToast.error(err?.detail || CUSTOMER_INTENT_SEND_FAILED_MESSAGE);
            setSubmitting(false);
        }
    };

    return (
        <SparkModalPortal open={open} onClose={close} width={CUSTOMER_INTENT_MODAL_WIDTH} destroyOnClose>
            <Modal
                open
                className="customer-intent-modal"
                title={
                    <span className="customer-intent-modal__title">
                        <SparkIcon slug="message-circle-icon" size="medium" />
                        {CUSTOMER_INTENT_MODAL_TITLE}
                    </span>
                }
                showSubtitle={false}
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                primaryLabel="Send"
                primaryDisabled={!canSubmit}
                tertiaryLabel="Cancel"
                tertiaryDisabled={submitting}
                onPrimaryClick={submit}
                onTertiaryClick={close}
                onCloseClick={close}
            >
                <form
                    className="customer-intent-modal__form"
                    onSubmit={(event) => {
                        event.preventDefault();
                        submit();
                    }}
                >
                    <div className="customer-intent-modal__section">
                        <Label label={CUSTOMER_INTENT_FEATURES_LABEL} />
                        <div role="group" aria-label={CUSTOMER_INTENT_FEATURES_LABEL} className="customer-intent-modal__features">
                            {CUSTOMER_INTENT_FEATURES.map((feature) => (
                                <ChoiceTile
                                    key={feature.value}
                                    className="customer-intent-feature-card"
                                    selectionMode="multiple"
                                    selected={features.includes(feature.value)}
                                    disabled={submitting}
                                    aria-label={feature.label}
                                    onClick={() => setFeatures((prev) => toggle(prev, feature.value))}
                                    label={
                                        <span className="customer-intent-feature-card__body">
                                            <TaskTypeIcon type={feature.iconType} size={32} showTooltip={false} />
                                            <span className="customer-intent-feature-card__title">{feature.label}</span>
                                            <span className="customer-intent-feature-card__description">
                                                {feature.description}
                                            </span>
                                        </span>
                                    }
                                />
                            ))}
                        </div>
                    </div>
                    <div className="customer-intent-modal__section">
                        <Label label={CUSTOMER_INTENT_AUTONOMY_LABEL} />
                        <div
                            role="radiogroup"
                            aria-label={CUSTOMER_INTENT_AUTONOMY_LABEL}
                            className="customer-intent-modal__autonomy"
                        >
                            {CUSTOMER_INTENT_AUTONOMY_LEVELS.map((level) => (
                                <ChoiceTile
                                    key={level.value}
                                    selectionMode="single"
                                    label={level.label}
                                    selected={autonomyLevel === level.value}
                                    disabled={submitting}
                                    onClick={() => setAutonomyLevel((prev) => (prev === level.value ? null : level.value))}
                                />
                            ))}
                        </div>
                    </div>
                    <InputTextArea
                        className="customer-intent-modal__field"
                        label={CUSTOMER_INTENT_SOMETHING_ELSE_LABEL}
                        placeholder={CUSTOMER_INTENT_SOMETHING_ELSE_PLACEHOLDER}
                        value={somethingElse}
                        onChange={(event) => setSomethingElse(event.target.value)}
                        maxLength={CUSTOMER_INTENT_SOMETHING_ELSE_MAX_LENGTH}
                        disabled={submitting}
                    />
                </form>
            </Modal>
        </SparkModalPortal>
    );
}
